import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { confirmPasswordReset } from "../utils/fetchFunctions";
import { isPasswordValid } from "../utils/passwordUtils";
import Button from "../components/Button";
import InputField from "../components/InputField";
import FormError from "../components/FormError";
import FormContainer from "../components/FormContainer";
import PasswordRules from "../components/PasswordRules";
import "./auth.css";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");

    if (!emailParam || !tokenParam) {
      setError("Invalid link. Please use the link from the email.");
    } else {
      setEmail(decodeURIComponent(emailParam));
      setToken(tokenParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    if (!isPasswordValid(password)) {
      setError("The password does not meet all requirements.");
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(email, token, password, confirmPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="auth-page">
        <FormContainer>
          <header>
            <h1 className="auth-title">Password reset</h1>
          </header>

          <div className="register-success-container">
            <div className="register-success-icon">✓</div>
            <p className="register-success-text-primary">
              Your password has been successfully reset.
            </p>
            <p className="register-success-text-secondary">
              You can now log in with your new password.
            </p>
            <Button
              className="auth-button"
              onClick={() => navigate("/login")}
            >
              To Login
            </Button>
          </div>
        </FormContainer>
      </main>
    );
  }

  if (!email || !token) {
    return (
      <main className="auth-page">
        <FormContainer>
          <header>
            <h1 className="auth-title">Invalid Link</h1>
          </header>

          <div className="register-success-container">
            <p className="register-success-text-primary">
              The password reset link is invalid or faulty.
            </p>
            <p className="register-success-text-secondary">
              Please use the link from the email or request a new link.
            </p>
            <Button
              className="auth-button"
              onClick={() => navigate("/forgot-password")}
            >
              Request new link
            </Button>
          </div>

          <footer className="auth-footer">
            Back to{" "}
            <Link to="/login">
              Login
            </Link>
          </footer>
        </FormContainer>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <FormContainer>
        <header>
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">
            Enter your new password.
          </p>
        </header>

        <form className="auth-form" onSubmit={handleSubmit}>
          <InputField
            id="password"
            type="password"
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <PasswordRules password={password} />

          <InputField
            id="confirmPassword"
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          <FormError message={error} />

          <Button
            type="submit"
            className="auth-button"
            disabled={loading}
            loading={loading}
            loadingText="Please wait…"
          >
            Reset Password
          </Button>
        </form>

        <footer className="auth-footer">
          Back to{" "}
          <a href="/login" onClick={(e) => {
            e.preventDefault();
            navigate("/login");
          }}>
            Login
          </a>
        </footer>
      </FormContainer>
    </main>
  );
};

export default ResetPassword;

